import React, { useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Paper,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ProfileIconSelector from './ProfileIconSelector';
import ProfileService from '../../services/ProfileService';
import { getIconById, getDefaultIcon } from '../../utils/profileIcons';
import EditIcon from '@mui/icons-material/Edit';
import MainLayout from '../layout/MainLayout';

const ProfileSettings: React.FC = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [iconSelectorOpen, setIconSelectorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const currentIcon = user?.profilePicture || getDefaultIcon().id;
  const iconData = getIconById(currentIcon) || getDefaultIcon();

  const handleIconSelect = async (iconId: string) => {
    try {
      setLoading(true);
      setError('');
      setSuccess(false);

      const response = await ProfileService.updateProfile({ profilePicture: iconId });
      
      if (response.success) {
        setUser(response.data.user);
        setSuccess(true);
        setIconSelectorOpen(false);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError('Failed to update profile icon');
      }
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.message || 'Failed to update profile icon');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout title="Profile Settings">
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, sm: 2, md: 3 } }}>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
            Profile icon updated successfully!
          </Alert>
        )}

        {/* Profile Icon Section */}
        <Card sx={{ mb: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 2 }}>
          <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: { xs: 2, md: 3 }, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
              Profile Icon
            </Typography>

            <Box display="flex" alignItems="center" gap={{ xs: 2, sm: 2.5, md: 3 }} flexWrap="wrap">
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  sx={{
                    width: { xs: 80, sm: 100, md: 120 },
                    height: { xs: 80, sm: 100, md: 120 },
                    bgcolor: 'primary.main',
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem' },
                    border: { xs: '3px solid', md: '4px solid' },
                    borderColor: 'primary.light',
                    boxShadow: 3
                  }}
                >
                  {iconData.emoji}
                </Avatar>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    p: 0.5,
                    boxShadow: 2
                  }}
                >
                  <EditIcon sx={{ fontSize: 20, color: 'white' }} />
                </Box>
              </Box>

              <Box flex={1}>
                <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
                  Current Icon: {iconData.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose from a diverse collection of cartoon icons to represent yourself
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setIconSelectorOpen(true)}
                  disabled={loading}
                >
                  Change Icon
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* User Info Card */}
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
              Account Information
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Username
              </Typography>
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 1
                }}
              >
                <Typography variant="body1" fontWeight={500}>
                  {user?.username}
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Email
              </Typography>
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 1
                }}
              >
                <Typography variant="body1" fontWeight={500}>
                  {user?.email}
                </Typography>
              </Paper>
            </Box>
          </CardContent>
        </Card>

        {/* Profile Icon Selector Dialog */}
        <ProfileIconSelector
          open={iconSelectorOpen}
          onClose={() => setIconSelectorOpen(false)}
          currentIcon={currentIcon}
          onSelect={handleIconSelect}
          loading={loading}
        />
      </Container>
    </MainLayout>
  );
};

export default ProfileSettings;
